import { Component, type ErrorInfo, type ReactNode } from 'react';

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Custom fallback; receives the error and a reset() to clear it. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** When this value changes, a caught error is cleared (e.g. the route path). */
  resetKey?: unknown;
  onError?: (error: Error, info: ErrorInfo) => void;
};

type ErrorBoundaryState = { error: Error | null };

/**
 * Catches render/lifecycle errors in the subtree so one throwing component
 * doesn't unmount the whole app to a blank white screen. Use one at the app
 * root (whole-app fault isolation) and one per route (a crashing page keeps the
 * shell/nav and can be retried). No external reporting is wired yet — errors go
 * to the console.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return <DefaultErrorFallback error={error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

const DefaultErrorFallback = ({ error, reset }: { error: Error; reset: () => void }) => (
  <div className="flex min-h-[240px] w-full flex-col items-center justify-center gap-4 p-8 text-center">
    <div className="space-y-1">
      <p className="text-base font-semibold text-foreground">Something went wrong</p>
      <p className="max-w-md text-sm text-foreground/60 break-words">{error.message}</p>
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={reset}
        className="rounded-2 border border-foreground/10 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
      >
        Retry
      </button>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-2 bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90"
      >
        Reload
      </button>
    </div>
  </div>
);

export default ErrorBoundary;
